import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";

export default function RefereeNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all"); // all, unread, read

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "notifications"),
      where("recipientId", "==", user.uid)
    );

    const unsub = onSnapshot(q, (snap) => {
      const notifs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by newest first, unread first
      notifs.sort((a, b) => {
        if (a.read !== b.read) return a.read ? 1 : -1;
        return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      });
      setNotifications(notifs);
    });

    return unsub;
  }, [user]);

  async function markAsRead(notificationId) {
    await updateDoc(doc(db, "notifications", notificationId), {
      read: true,
    });
  }

  async function markAllAsRead() {
    const unreadNotifs = notifications.filter(n => !n.read);
    await Promise.all(
      unreadNotifs.map(n => updateDoc(doc(db, "notifications", n.id), { read: true }))
    );
  }

  async function deleteNotification(notificationId) {
    if (window.confirm("Delete this notification?")) {
      await deleteDoc(doc(db, "notifications", notificationId));
    }
  }

  async function clearAll() {
    if (window.confirm("Delete all notifications? This cannot be undone.")) {
      await Promise.all(
        notifications.map(n => deleteDoc(doc(db, "notifications", n.id)))
      );
    }
  }

  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "read") return n.read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold">My Notifications</h2>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className={`px-4 py-2 rounded text-sm font-semibold ${
              unreadCount > 0
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Mark All Read
          </button>
          <button
            onClick={clearAll}
            disabled={notifications.length === 0}
            className={`px-4 py-2 rounded text-sm font-semibold ${
              notifications.length > 0
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
            filter === "all"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          All ({notifications.length})
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
            filter === "unread"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Unread ({unreadCount})
        </button>
        <button
          onClick={() => setFilter("read")}
          className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
            filter === "read"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-600 hover:text-gray-900"
          }`}
        >
          Read ({notifications.length - unreadCount})
        </button>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {filter === "unread" ? "No unread notifications" : filter === "read" ? "No read notifications" : "No notifications yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notif) => (
            <div
              key={notif.id}
              className={`p-4 rounded-lg shadow border-l-4 transition-all ${
                notif.read
                  ? "bg-gray-50 border-gray-300"
                  : "bg-blue-50 border-blue-600"
              }`}
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {!notif.read && (
                      <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    )}
                    <h3 className={`font-bold ${notif.read ? "text-gray-700" : "text-blue-900"}`}>
                      {notif.title}
                    </h3>
                  </div>
                  <p className={`text-sm ${notif.read ? "text-gray-600" : "text-gray-800"}`}>
                    {notif.message}
                  </p>
                  {notif.createdAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(notif.createdAt.seconds * 1000).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {!notif.read && (
                    <button
                      onClick={() => markAsRead(notif.id)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
                    >
                      Mark Read
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notif.id)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
